import {
	PropsWithChildren,
	ReactElement,
	useContext
} from "react";
import { ActionListItem } from "../../ActionList/ActionListItem";
import { FontIconName, SvgIconValue } from "../../Icon/Icon";
import { ButtonBase } from "../../ButtonBase/ButtonBase";
import {
	CollapsibleListItemContext,
	CollapsibleListItemRenderMode
} from "../../CollapsibleList/CollapsibleList";
import styles from "./ActionBarButton.module.less";
import { ActionBarButtonIcon } from "../ActionBarButtonIcon/ActionBarButtonIcon";
import { useCallbackWithoutArguments, useKeyboardListNavigationItem } from "client/Hooks";
import { Key } from "client/Misc/commonTypes";

export type ActionBarButtonProps = {
	icon?: FontIconName | SvgIconValue;
	text: string;
	onClick: () => void;
	trackingId?: string;
	isDisabled?: boolean;
};

export const ActionBarButton = (props: ActionBarButtonProps): ReactElement => {
	const {
		icon,
		text,
		onClick,
		trackingId,
		isDisabled = false
	} = props;

	const {
		renderMode,
		index,
		tabIndex
	} = useContext(CollapsibleListItemContext);

	const clickHandler: (() => void) | undefined = useCallbackWithoutArguments(onClick);

	if (renderMode === CollapsibleListItemRenderMode.Collapsed) {
		return (
			<ActionListItem
				text={text}
				icon={icon}
				onClick={onClick}
				trackingId={trackingId}
				isDisabled={isDisabled}
			/>
		);
	}

	return (
		<NormalButton
			onClick={clickHandler}
			trackingId={trackingId}
			icon={icon}
			tabIndex={tabIndex}
			index={index}
			isDisabled={isDisabled}
		>
			{text}
		</NormalButton>
	);
};

ActionBarButton.displayName = "ActionBarButton";

type NormalButtonProps = {
	onClick: () => void;
	trackingId: string | undefined;
	icon?: FontIconName | SvgIconValue;
	index: number;
	tabIndex: number;
	isDisabled: boolean;
};

const NormalButton = (props: PropsWithChildren<NormalButtonProps>): ReactElement => {
	const {
		children,
		onClick,
		trackingId,
		icon,
		index,
		tabIndex,
		isDisabled
	} = props;

	const {
		ref,
		onKeyDown
	} = useKeyboardListNavigationItem({
		itemIndex: isDisabled ? -1 : index,
		onExecute: onClick,
		executeKey: [Key.Enter, Key.Space]
	});

	return (
		<ButtonBase
			className={styles.root}
			onClick={onClick}
			disabled={isDisabled}
			ref={ref}
			onKeyDown={onKeyDown}
			trackingId={trackingId}
			tabIndex={tabIndex}
		>
			{icon && (
				<ActionBarButtonIcon
					icon={icon}
					isDisabled={isDisabled}
				/>
			)}
			<span>
				{children}
			</span>
		</ButtonBase>
	);
};
NormalButton.displayName = "NormalButton";
